import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";
import { Brand } from "../components/Shell";
import { HowItWorks } from "../components/HowItWorks";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/PasswordInput";
import { Label } from "../components/ui/label";

const BG =
  "https://images.pexels.com/photos/1313534/pexels-photo-1313534.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex min-h-screen">
      <div className="flex w-full flex-col justify-center px-6 py-14 sm:px-12 lg:w-[54%] lg:px-20">
        <div className="ct-rise mx-auto w-full max-w-md">
          <Brand />
          <h1 className="mt-14 font-display text-4xl font-black leading-[1.05] tracking-tight text-slate-900 sm:text-5xl">
            Credit &amp; trade
            <br />
            reference verification
          </h1>
          <p className="mt-4 text-base text-slate-600">
            Sign in to manage companies, customers and their trade references.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Email address
              </Label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid="login-email-input"
                className="mt-2 h-11 border-slate-300 focus-visible:ring-blue-600"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Password
              </Label>
              <PasswordInput
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                testId="login-password-input"
                className="mt-2"
              />
            </div>
            {error && (
              <div
                className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                data-testid="login-error"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="h-11 w-full rounded-md bg-blue-600 text-[15px] font-semibold transition-colors duration-200 hover:bg-blue-700 active:scale-[0.99]"
            >
              {busy ? "Signing in…" : "Sign in"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-600">
            New to ConfirmTerms?{" "}
            <Link
              to="/signup"
              data-testid="login-signup-link"
              className="font-semibold text-blue-600 hover:text-blue-700"
            >
              Create a free account
            </Link>
          </p>

          <div className="mt-8 flex items-start gap-3 rounded-md border border-slate-200 bg-white p-4">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
            <p className="text-sm text-slate-600">
              Are you a trade reference? Use the unique link and access password sent to you — no
              account required.
            </p>
          </div>
        </div>
      </div>
      <div className="relative hidden lg:block lg:w-[46%]">
        <img src={BG} alt="Corporate building facade" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/45" />
        <div className="absolute bottom-14 left-12 right-12 text-white">
          <div className="text-xs font-semibold uppercase tracking-[0.24em] text-blue-200">
            ConfirmTerms.com
          </div>
          <p className="mt-4 font-display text-2xl font-extrabold leading-snug tracking-tight">
            Collect verified trade references before you extend terms.
          </p>
        </div>
      </div>
      </div>
      <HowItWorks />
    </div>
  );
}
